import { useState, type ReactNode } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/components/ui/card';
import { Skeleton } from '@/shared/components/ui/skeleton';
import {
  RANGO_LABELS,
  useVentasDashboard,
  type RangoPreset,
  type VentasDashboard,
} from './ventasApi';
import { fmtARS, fmtARSCompact, fmtInt, fmtVar, varSign } from './dashFormat';
import {
  buildCatColor,
  CategoriaLegend,
  EvolucionVentasChart,
  RankBars,
  SERIE_AZUL,
  type RankRow,
} from './ventasCharts';
import { DeltaBadge, EmptyCard, SegBar, StatInline } from './dashUi';
import { VentasPorTipoCard } from './VentasPorTipoCard';
import { VendedoresView } from './VendedoresView';

const RANGOS: RangoPreset[] = ['3m', '6m', '12m', 'ytd'];

export function VentasSub({ nav }: { nav?: ReactNode }) {
  const [modo, setModo] = useState<'resumen' | 'vendedores'>('resumen');
  const [rango, setRango] = useState<RangoPreset>('12m');

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-2">
          {nav}
          <SegBar
            value={modo}
            onChange={setModo}
            options={[
              { id: 'resumen', label: 'Resumen' },
              { id: 'vendedores', label: 'Vendedores' },
            ]}
          />
        </div>
        <SegBar
          value={rango}
          onChange={setRango}
          options={RANGOS.map((r) => ({ id: r, label: RANGO_LABELS[r] }))}
        />
      </div>

      {modo === 'resumen' ? <ResumenView rango={rango} /> : <VendedoresView rango={rango} />}
    </div>
  );
}

function ResumenView({ rango }: { rango: RangoPreset }) {
  const [medida, setMedida] = useState<'monto' | 'unidades'>('monto');
  const q = useVentasDashboard(rango);

  if (q.isError) {
    return <EmptyCard>No se pudieron cargar los datos de ventas. Reintentá en unos segundos.</EmptyCard>;
  }
  if (q.isLoading || !q.data) {
    return <VentasSkeleton />;
  }
  return <VentasLoaded data={q.data} rango={rango} medida={medida} setMedida={setMedida} />;
}

function VentasLoaded({
  data,
  rango,
  medida,
  setMedida,
}: {
  data: VentasDashboard;
  rango: RangoPreset;
  medida: 'monto' | 'unidades';
  setMedida: (m: 'monto' | 'unidades') => void;
}) {
  const { mesEnCurso: mc, periodo } = data;
  const catColor = buildCatColor(data.categorias);

  const clientesRows: RankRow[] = data.topClientes.map((c) => ({
    label: c.cliente,
    value: c.total,
    color: SERIE_AZUL,
    valueText: fmtARSCompact(c.total),
    sub: `${fmtInt(c.operaciones)} ${c.operaciones === 1 ? 'venta' : 'ventas'}`,
  }));

  const articulosRows: RankRow[] = data.topArticulos.map((a) => ({
    label: a.articulo.toUpperCase(),
    value: a.unidades,
    color: catColor(a.categoria),
    valueText: `${fmtInt(a.unidades)} u`,
    sub: `${a.categoria} · ${fmtARSCompact(a.total)}`,
  }));

  const vendedoresRows: RankRow[] = data.topVendedores.slice(0, 10).map((v) => ({
    label: v.vendedor,
    value: v.total,
    color: SERIE_AZUL,
    valueText: fmtARSCompact(v.total),
    sub: `${fmtInt(v.unidades)} u · ${fmtInt(v.operaciones)} ${v.operaciones === 1 ? 'venta' : 'ventas'}`,
  }));

  return (
    <>
      {/* KPIs combinados */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <Card>
          <CardContent className="flex flex-col gap-3 p-5">
            <span className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
              Mes en curso · {mc.label} <span className="normal-case">(al día {mc.diaCorte})</span>
            </span>
            <div className="flex items-baseline gap-3">
              <span className="text-3xl font-semibold tracking-tight text-foreground">
                {fmtARS(mc.total)}
              </span>
              <DeltaBadge delta={fmtVar(mc.total, mc.totalPrev)} sign={varSign(mc.total, mc.totalPrev)} />
            </div>
            <span className="text-xs text-muted-foreground">
              vs. mismo tramo mes anterior: {fmtARSCompact(mc.totalPrev)}
            </span>
            <div className="mt-1 grid grid-cols-4 gap-3 border-t pt-3">
              <StatInline
                label="Unidades"
                value={`${fmtInt(mc.unidades)} u`}
                delta={fmtVar(mc.unidades, mc.unidadesPrev)}
                sign={varSign(mc.unidades, mc.unidadesPrev)}
              />
              <StatInline label="Operaciones" value={fmtInt(mc.operaciones)} />
              <StatInline label="Ticket prom." value={fmtARSCompact(mc.ticket)} />
              <StatInline label="Clientes" value={fmtInt(mc.clientes)} />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="flex flex-col gap-3 p-5">
            <span className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
              Acumulado · {RANGO_LABELS[rango]}
            </span>
            <div className="flex items-baseline gap-3">
              <span className="text-3xl font-semibold tracking-tight text-foreground">
                {fmtARS(periodo.total)}
              </span>
              <span className="text-xs text-muted-foreground">
                {fmtARSCompact(periodo.promedioMensual)}/mes prom.
              </span>
            </div>
            <span className="text-xs text-muted-foreground">
              {periodo.meses} {periodo.meses === 1 ? 'mes' : 'meses'} · ventas con pago total
            </span>
            <div className="mt-1 grid grid-cols-4 gap-3 border-t pt-3">
              <StatInline label="Unidades" value={`${fmtInt(periodo.unidades)} u`} />
              <StatInline label="Operaciones" value={fmtInt(periodo.operaciones)} />
              <StatInline label="Ticket prom." value={fmtARSCompact(periodo.ticket)} />
              <StatInline label="Clientes" value={fmtInt(periodo.clientes)} />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Evolución */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between gap-3 space-y-0 pb-2">
          <CardTitle className="text-base">Evolución de ventas (por mes de pago)</CardTitle>
          <SegBar
            value={medida}
            onChange={setMedida}
            options={[
              { id: 'monto', label: '$' },
              { id: 'unidades', label: 'Q' },
            ]}
          />
        </CardHeader>
        <CardContent>
          <EvolucionVentasChart series={data.series} medida={medida} />
        </CardContent>
      </Card>

      {/* Ventas por tipo (Hornos / Accesorios / Repuestos / …) */}
      <VentasPorTipoCard data={data} rango={rango} />

      {/* Vendedores */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Ventas por vendedor</CardTitle>
        </CardHeader>
        <CardContent>
          <RankBars rows={vendedoresRows} total={periodo.total} emptyText="Sin ventas en el período." />
        </CardContent>
      </Card>

      {/* Tops */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Top 10 clientes por facturación</CardTitle>
          </CardHeader>
          <CardContent>
            <RankBars rows={clientesRows} total={periodo.total} emptyText="Sin ventas en el período." />
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Top 10 artículos más vendidos</CardTitle>
          </CardHeader>
          <CardContent className="flex flex-col gap-3">
            {data.categorias.length > 0 && (
              <CategoriaLegend categorias={data.categorias} color={catColor} />
            )}
            <RankBars rows={articulosRows} total={periodo.unidades} emptyText="Sin ventas en el período." />
          </CardContent>
        </Card>
      </div>
    </>
  );
}

function VentasSkeleton() {
  return (
    <>
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        {[0, 1].map((i) => (
          <Card key={i}>
            <CardContent className="flex flex-col gap-3 p-5">
              <Skeleton className="h-3 w-40" />
              <Skeleton className="h-8 w-44" />
              <Skeleton className="h-16 w-full" />
            </CardContent>
          </Card>
        ))}
      </div>
      <Card>
        <CardContent className="p-5">
          <Skeleton className="h-[260px] w-full" />
        </CardContent>
      </Card>
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        {[0, 1].map((i) => (
          <Card key={i}>
            <CardContent className="flex flex-col gap-3 p-5">
              {Array.from({ length: 6 }).map((_, j) => (
                <Skeleton key={j} className="h-5 w-full" />
              ))}
            </CardContent>
          </Card>
        ))}
      </div>
    </>
  );
}
